import { ExpandMore, ManageSearch, SavedSearch, SearchOutlined, SearchTwoTone, TravelExplore } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Alert, Autocomplete, Box, Card, CardContent, Chip, Grid, Paper, Switch, TextField, Theme, Tooltip, Typography, useTheme } from "@mui/material/";
import { cloneDeep } from "lodash";
import { useContext, useEffect, useState } from "react";
import AnimateHeight from "react-animate-height";
import { SearchContext } from "../../contexts/SearchContext";
import { IMaterialItem } from "../../interfaces";
import { DatePicker } from "../Shared/FormControls/DatePicker";
import './index.scss';
import { WidgetHeader } from "./WidgetHeader";


const AccordionStyle = (theme: Theme) => {
    return {
        boxShadow: 'none',
        borderRadius: '0px !important',

        '& .MuiAccordionSummary-root': {
            padding: 0,
        },
    }
}

export const AdvancedSearchWidget = () => {

    const searchContext = useContext(SearchContext);

    const onKeyword = (item: IMaterialItem, remove?: boolean) => {
        let keywords = cloneDeep(searchContext.keywords);

        if (remove) {
            keywords = keywords.filter(x => JSON.stringify(x) !== JSON.stringify(item));
        } else {
            keywords.push(item);
        }

        searchContext.search(false, keywords);

    }

    const onAdvanced = (propertyName: string, value: string | null) => {
        onAdvancedArray([{ propertyName, value }]);
    }

    const onAdvancedArray = (properties: { propertyName: string, value: string | null }[]) => {
        let keywords = cloneDeep(searchContext.keywords);
        properties.forEach(p => {
            //start by removing existing advanced keywords
            keywords = keywords.filter(x => x.propertyName !== p.propertyName);

            if (p.value !== null) {
                //add in the new value
                keywords.push({ ...p });
            }

        });

        //update the keywords
        searchContext.search(false, keywords);
    }

    const [expanded, setExpanded] = useState<boolean>(true);
    const [storageReasonEnabled, setStorageReasonEnabled] = useState<boolean>(false);
    const [restrictionsEnabled, setRestrictionsEnabled] = useState<boolean>(false);
    const [hasPhotosEnabled, setHasPhotosEnabled] = useState<boolean>(false);
    const [arrivedEnabled, setArrivedEnabled] = useState<boolean>(false);
    const [overdueEnabled, setOverdueEnabled] = useState<boolean>(false);
    const [consumedEnabled, setConsumedEnabled] = useState<boolean>(false);
    const [dispatchedEnabled, setDispatchedEnabled] = useState<boolean>(false);
    const [expectedReturnEnabled, setExpectedReturnEnabled] = useState<boolean>(false);

    const sparetypesSuggestions = searchContext.suggestions.filter(x => x.propertyName === "sparetypes");
    const vehicleClassesSuggestions = searchContext.suggestions.filter(x => x.propertyName === "vehicleclasses");
    const l1Suggestions = searchContext.suggestions.filter(x => x.propertyName === "l1");
    const l2Suggestions = searchContext.suggestions.filter(x => x.propertyName === "l2");
    const siteSuggestions = searchContext.suggestions.filter(x => x.propertyName === "sites");
    const agreementSuggestions = searchContext.suggestions.filter(x => x.propertyName === "commercial-agreements");
    const expectedReturnSuggestions = searchContext.suggestions.filter(x => x.propertyName === "expectedmaterialreturnstates");




    const onToggleRestrictions = () => {

        if (restrictionsEnabled) {
            //we are turning off this feature
            onAdvanced("Restrictions", null);
        } else {
            //we are turning on this feature
            onAdvanced("Restrictions", "Yes");
        }

        setRestrictionsEnabled(!restrictionsEnabled);

    }
    const onToggleHasPhotos = () => {

        if (hasPhotosEnabled) {
            //we are turning off this feature
            onAdvanced("Photos", null);
        } else {
            //we are turning on this feature
            onAdvanced("Photos", "Yes");
        }

        setHasPhotosEnabled(!hasPhotosEnabled);

    }

    const onToggleStorageReason = () => {

        if (storageReasonEnabled) {
            //we are turning off this feature
            onAdvanced("StorageReason", null);
        } else {
            //we are turning on this feature
            onAdvanced("StorageReason", "Yes");
        }

        setStorageReasonEnabled(!storageReasonEnabled);

    }


    const onToggleArrived = () => {
        if (arrivedEnabled) {
            //we are turning off this feature
            onAdvancedArray([
                { propertyName: "Arrived (From)", value: null },
                { propertyName: "Arrived (To)", value: null }
            ]);

        } else {
            //we are turning on this feature
            let initDate = (new Date()).toLocaleDateString('en-GB');
            onAdvancedArray([
                { propertyName: "Arrived (From)", value: initDate },
                { propertyName: "Arrived (To)", value: initDate }
            ]);
        }

        setArrivedEnabled(!arrivedEnabled);
    }


    const onToggleMaterialOut = () => {
        searchContext.setMaterialOut(!searchContext.materialOut);
    }
    const onToggleExpectedReturn = () => {

        if (restrictionsEnabled) {
            //we are turning off this feature
            onAdvanced("Expected Return", null);
        } else {
            //we are turning on this feature
            onAdvanced("Expected Return", "Yes");
        }

        setExpectedReturnEnabled(!expectedReturnEnabled);

    }

    const onToggleOverdue = () => {

        if (overdueEnabled) {
            //we are turning off this feature
            onAdvanced("Overdue", null);
        } else {
            //we are turning on this feature
            onAdvanced("Overdue", "Yes");
        }

        setOverdueEnabled(!overdueEnabled);

    }
    const onToggleConsumed = () => {

        if (consumedEnabled) {
            //we are turning off this feature
            onAdvanced("Consumed", null);
        } else {
            //we are turning on this feature
            onAdvanced("Consumed", "Yes");
        }

        setConsumedEnabled(!consumedEnabled);

    }
    const onToggleDispatched = () => {
        if (dispatchedEnabled) {
            //we are turning off this feature
            onAdvancedArray([
                { propertyName: "Dispatched (From)", value: null },
                { propertyName: "Dispatched (To)", value: null }
            ]);

        } else {
            //we are turning on this feature
            let initDate = (new Date()).toLocaleDateString('en-GB');
            onAdvancedArray([
                { propertyName: "Dispatched (From)", value: initDate },
                { propertyName: "Dispatched (To)", value: initDate }
            ]);
        }

        setDispatchedEnabled(!dispatchedEnabled);
    }


    const getValue = (propertyName: string) => {
        let values = searchContext.keywords.filter(x => x?.propertyName === propertyName);

        if (values.length > 0) {
            return values[0].value
        }

        return "";

    }

    const isActive = (item: IMaterialItem) => {

        return searchContext.keywords.filter(x => JSON.stringify(x) === JSON.stringify(item)).length > 0;

    }

    const advancedLabelStyles = { display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center" };

    useEffect(() => {

        let arrivedDateKeywords = searchContext.keywords.filter(x => (x.id == null && x.propertyName?.startsWith("Arrived")));
        let restrictionsKeywords = searchContext.keywords.filter(x => (x.id == null && x.propertyName?.startsWith("Restrictions")));

        if (arrivedDateKeywords.length > 0)
            setArrivedEnabled(true);


        if (restrictionsKeywords.length > 0)
            setRestrictionsEnabled(true);


    }, [searchContext.keywords])


    return (
        <Card elevation={3} className="dashboard-widget" sx={{ height: "auto" }}>
            <WidgetHeader><ManageSearch sx={{ mr: 1 }} /> Advanced Search
                <Switch
                    sx={{ float: "right" }}
                    size="small"
                    color="secondary"
                    checked={expanded}
                    onChange={() => setExpanded(!expanded)}
                />
            </WidgetHeader>
            <AnimateHeight height={expanded ? "auto" : 0} duration={500}>

                <CardContent sx={{ height: "65%", overflowY: "auto", scrollbarWidth: "thin" }}>
                    <Accordion
                        sx={AccordionStyle(useTheme())}
                        disableGutters
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore color="primary" />}
                        >
                            <Typography color="primary">
                                Description Level 1 <small style={{ opacity: .5 }}>({l1Suggestions.length})</small>
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container>
                                {l1Suggestions.map((keyword) => {

                                    let active = isActive(keyword);

                                    return <Tooltip placement="right" title={keyword.value as string} key={keyword.id}>
                                        <Chip
                                            variant={active ? "filled" : "outlined"}
                                            onDelete={active ? () => onKeyword(keyword, true) : undefined}
                                            onClick={active ? undefined : () => onKeyword(keyword)}
                                            color="primary"
                                            label={keyword.value}
                                            key={(keyword.id as string + keyword.value)}
                                            sx={{ margin: '.1rem' }}
                                        /></Tooltip>

                                })}
                            </Grid>

                        </AccordionDetails>

                    </Accordion>

                    <Accordion
                        sx={AccordionStyle(useTheme())}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore color="primary" />}
                        >
                            <Typography color="primary">
                                Description Level 2 <small style={{ opacity: .5 }}>({l2Suggestions.length})</small>
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container>
                                {l2Suggestions.map((keyword) => {

                                    let active = isActive(keyword);

                                    return <Tooltip placement="right" title={keyword.value as string} key={keyword.id}>
                                        <Chip
                                            variant={active ? "filled" : "outlined"}
                                            onDelete={active ? () => onKeyword(keyword, true) : undefined}
                                            onClick={active ? undefined : () => onKeyword(keyword)}
                                            color="primary"
                                            label={keyword.value}
                                            key={(keyword.id as string + keyword.value)}
                                            sx={{ margin: '.1rem' }}
                                        /></Tooltip>

                                })}
                            </Grid>

                        </AccordionDetails>

                    </Accordion>
                    <Accordion
                        sx={AccordionStyle(useTheme())}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore color="primary" />}
                        >
                            <Typography color="primary">
                                Site <small style={{ opacity: .5 }}>({siteSuggestions.length})</small>
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container>
                                {siteSuggestions.map((keyword) => {

                                    let active = isActive(keyword);

                                    return <Tooltip placement="right" title={keyword.value as string} key={keyword.id}>
                                        <Chip
                                            variant={active ? "filled" : "outlined"}
                                            onDelete={active ? () => onKeyword(keyword, true) : undefined}
                                            onClick={active ? undefined : () => onKeyword(keyword)}
                                            color="primary"
                                            label={keyword.value}
                                            key={(keyword.id as string + keyword.value)}
                                            sx={{ margin: '.1rem' }}
                                        /></Tooltip>

                                })}
                            </Grid>

                        </AccordionDetails>

                    </Accordion>

                    <Accordion
                        sx={AccordionStyle(useTheme())}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore color="primary" />}
                        >
                            <Typography color="primary">
                                Spare Type <small style={{ opacity: .5 }}>({sparetypesSuggestions.length})</small>
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container>
                                {sparetypesSuggestions.map((keyword) => {

                                    let active = isActive(keyword);

                                    return <Tooltip placement="right" title={keyword.value as string} key={keyword.id}>
                                        <Chip
                                            variant={active ? "filled" : "outlined"}
                                            onDelete={active ? () => onKeyword(keyword, true) : undefined}
                                            onClick={active ? undefined : () => onKeyword(keyword)}
                                            color="primary"
                                            label={keyword.value}
                                            key={(keyword.id as string + keyword.value)}
                                            sx={{ margin: '.1rem' }}
                                        /></Tooltip>

                                })}
                            </Grid>

                        </AccordionDetails>

                    </Accordion>
                    <Accordion
                        sx={AccordionStyle(useTheme())}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore color="primary" />}
                        >
                            <Typography color="primary">
                                Vehicle Class <small style={{ opacity: .5 }}>({vehicleClassesSuggestions.length})</small>
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container>
                                {vehicleClassesSuggestions.map((keyword) => {



                                    let active = isActive(keyword);

                                    return <Tooltip placement="right" title={keyword.value as string} key={keyword.id}>
                                        <Chip
                                            variant={active ? "filled" : "outlined"}
                                            onDelete={active ? () => onKeyword(keyword, true) : undefined}
                                            onClick={active ? undefined : () => onKeyword(keyword)}
                                            color="primary"
                                            label={keyword.value}
                                            key={(keyword.id as string + keyword.value)}
                                            sx={{ margin: '.1rem' }}
                                        /></Tooltip>

                                })}
                            </Grid>

                        </AccordionDetails>

                    </Accordion>
                    <Accordion
                        sx={AccordionStyle(useTheme())}
                        expanded={storageReasonEnabled}
                    >
                        <AccordionSummary
                            onClick={onToggleStorageReason}
                        >
                            <Typography color="primary" sx={advancedLabelStyles}>
                                Storage Reason <Switch
                                    checked={storageReasonEnabled}
                                    onChange={onToggleStorageReason} />
                            </Typography>

                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container>
                                <Grid item xs={10}>
                                    <Autocomplete
                                        id="material-storagereason"
                                        options={["Yes", "No", ""]}
                                        value={getValue("StorageReason")}
                                        onChange={(_, value) => onAdvanced("StorageReason", value)}
                                        disabled={!storageReasonEnabled}
                                        renderInput={(params) =>
                                            <TextField
                                                {...params}
                                                variant="standard"
                                            />}
                                    />

                                </Grid>
                            </Grid>

                        </AccordionDetails>

                    </Accordion>
                    <Accordion
                        sx={AccordionStyle(useTheme())}
                        expanded={restrictionsEnabled}
                    >
                        <AccordionSummary
                            onClick={onToggleRestrictions}
                        >
                            <Typography color="primary" sx={advancedLabelStyles}>
                                Restrictions <Switch
                                    checked={restrictionsEnabled}
                                    onChange={onToggleRestrictions} />
                            </Typography>

                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container>
                                <Grid item xs={10}>
                                    <Autocomplete
                                        id="material-restrictions"
                                        options={["Yes", "No", ""]}
                                        value={getValue("Restrictions")}
                                        onChange={(_, value) => onAdvanced("Restrictions", value)}
                                        disabled={!restrictionsEnabled}
                                        renderInput={(params) =>
                                            <TextField
                                                {...params}
                                                variant="standard"
                                            />}
                                    />

                                </Grid>
                            </Grid>

                        </AccordionDetails>

                    </Accordion>
                    <Accordion
                        sx={AccordionStyle(useTheme())}
                        expanded={arrivedEnabled}
                    >
                        <AccordionSummary
                            onClick={onToggleArrived}
                        >
                            <Typography color="primary" sx={advancedLabelStyles}>
                                Arrived Date <Switch
                                    checked={arrivedEnabled}
                                    onChange={onToggleArrived} />
                            </Typography>

                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container>
                                <Grid item xs={10}>
                                    <Box sx={{ mb: 2 }}>
                                        <DatePicker
                                            variant="standard"
                                            label="From"
                                            onChange={(value) => onAdvanced("Arrived (From)", value)}
                                            value={getValue("Arrived (From)")}
                                            nullable={false}
                                        />
                                    </Box>

                                    <DatePicker
                                        variant="standard"
                                        label="To"
                                        onChange={(value) => onAdvanced("Arrived (To)", value)}
                                        value={getValue("Arrived (To)")}
                                        nullable={false}
                                    />
                                </Grid>
                            </Grid>

                        </AccordionDetails>

                    </Accordion>
                    <Accordion
                        sx={AccordionStyle(useTheme())}
                        expanded={hasPhotosEnabled}
                    >
                        <AccordionSummary
                            onClick={onToggleHasPhotos}
                        >
                            <Typography color="primary" sx={advancedLabelStyles}>
                                Has Photos <Switch
                                    checked={hasPhotosEnabled}
                                    onChange={onToggleHasPhotos} />
                            </Typography>

                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container>
                                <Grid item xs={10}>
                                    <Autocomplete
                                        id="material-hasphotos"
                                        options={["Yes", "No", ""]}
                                        value={getValue("Photos")}
                                        onChange={(_, value) => onAdvanced("Photos", value)}
                                        disabled={!hasPhotosEnabled}
                                        renderInput={(params) =>
                                            <TextField
                                                {...params}
                                                variant="standard"
                                            />}
                                    />

                                </Grid>
                            </Grid>

                        </AccordionDetails>

                    </Accordion>
                    <Alert
                        severity="warning"
                        variant="outlined"
                        icon={<></>}
                        sx={{ m: 0, w: 100, '& .MuiAlert-icon': { m: 0 }, '& .MuiAlert-message': { width: '100%' } }}
                    >
                        <Typography sx={{ cursor: 'pointer', ...advancedLabelStyles }} display="flex" alignItems="space-between" onClick={onToggleMaterialOut}>
                            <span><TravelExplore sx={{ mr: 2 }} />Material Out</span> <Switch
                                color="warning"
                                checked={searchContext.materialOut}
                                onChange={onToggleMaterialOut} />
                        </Typography>
                        {searchContext.materialOut && <>
                            <hr />
                            <Accordion
                                sx={AccordionStyle(useTheme())}
                                disableGutters
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMore color="primary" />}
                                >
                                    <Typography color="primary">
                                        Commercial Agreement <small style={{ opacity: .5 }}>({agreementSuggestions.length})</small>
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container>
                                        {agreementSuggestions.map((keyword) => {

                                            let active = isActive(keyword);

                                            return <Tooltip placement="right" title={keyword.value as string} key={keyword.id}>
                                                <Chip
                                                    variant={active ? "filled" : "outlined"}
                                                    onDelete={active ? () => onKeyword(keyword, true) : undefined}
                                                    onClick={active ? undefined : () => onKeyword(keyword)}
                                                    color="primary"
                                                    label={keyword.value}
                                                    key={(keyword.id as string + keyword.value)}
                                                    sx={{ margin: '.1rem' }}
                                                /></Tooltip>

                                        })}
                                    </Grid>

                                </AccordionDetails>

                            </Accordion>
                            <Accordion
                                sx={AccordionStyle(useTheme())}
                                disableGutters
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMore color="primary" />}
                                >
                                    <Typography color="primary">
                                        Expected Return State <small style={{ opacity: .5 }}>({expectedReturnSuggestions.length})</small>
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container>
                                        {expectedReturnSuggestions.map((keyword) => {

                                            let active = isActive(keyword);

                                            return <Tooltip placement="right" title={keyword.value as string} key={keyword.id}>
                                                <Chip
                                                    variant={active ? "filled" : "outlined"}
                                                    onDelete={active ? () => onKeyword(keyword, true) : undefined}
                                                    onClick={active ? undefined : () => onKeyword(keyword)}
                                                    color="primary"
                                                    label={keyword.value}
                                                    key={(keyword.id as string + keyword.value)}
                                                    sx={{ margin: '.1rem' }}
                                                /></Tooltip>

                                        })}
                                    </Grid>

                                </AccordionDetails>

                            </Accordion>
                            <Accordion
                                sx={AccordionStyle(useTheme())}
                                expanded={expectedReturnEnabled}
                            >
                                <AccordionSummary
                                    onClick={onToggleExpectedReturn}
                                >
                                    <Typography color="primary" sx={advancedLabelStyles}>
                                        Expected Return <Switch
                                            checked={expectedReturnEnabled}
                                            onChange={onToggleExpectedReturn} />
                                    </Typography>

                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container>
                                        <Grid item xs={10}>
                                            <Autocomplete
                                                id="material-expectedreturn"
                                                options={["Yes", "No", ""]}
                                                value={getValue("Expected Return")}
                                                onChange={(_, value) => onAdvanced("Expected Return", value)}
                                                disabled={!expectedReturnEnabled}
                                                renderInput={(params) =>
                                                    <TextField
                                                        {...params}
                                                        variant="standard"
                                                    />}
                                            />

                                        </Grid>
                                    </Grid>

                                </AccordionDetails>

                            </Accordion>

                            <Accordion
                                sx={AccordionStyle(useTheme())}
                                expanded={overdueEnabled}
                            >
                                <AccordionSummary
                                    onClick={onToggleOverdue}
                                >
                                    <Typography color="primary" sx={advancedLabelStyles}>
                                        Overdue <Switch
                                            checked={overdueEnabled}
                                            onChange={onToggleOverdue} />
                                    </Typography>

                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container>
                                        <Grid item xs={10}>
                                            <Autocomplete
                                                id="material-overdue"
                                                options={["Yes", "No", ""]}
                                                value={getValue("Overdue")}
                                                onChange={(_, value) => onAdvanced("Overdue", value)}
                                                disabled={!overdueEnabled}
                                                renderInput={(params) =>
                                                    <TextField
                                                        {...params}
                                                        variant="standard"
                                                    />}
                                            />

                                        </Grid>
                                    </Grid>

                                </AccordionDetails>

                            </Accordion>
                            <Accordion
                                sx={AccordionStyle(useTheme())}
                                expanded={consumedEnabled}
                            >
                                <AccordionSummary
                                    onClick={onToggleConsumed}
                                >
                                    <Typography color="primary" sx={advancedLabelStyles}>
                                        Consumed <Switch
                                            checked={consumedEnabled}
                                            onChange={onToggleConsumed} />
                                    </Typography>

                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container>
                                        <Grid item xs={10}>
                                            <Autocomplete
                                                id="material-consumed"
                                                options={["Yes", "No", ""]}
                                                value={getValue("Consumed")}
                                                onChange={(_, value) => onAdvanced("Consumed", value)}
                                                disabled={!consumedEnabled}
                                                renderInput={(params) =>
                                                    <TextField
                                                        {...params}
                                                        variant="standard"
                                                    />}
                                            />

                                        </Grid>
                                    </Grid>

                                </AccordionDetails>

                            </Accordion>
                            <Accordion
                                sx={AccordionStyle(useTheme())}
                                expanded={dispatchedEnabled}
                            >
                                <AccordionSummary
                                    onClick={onToggleDispatched}
                                >
                                    <Typography color="primary" sx={advancedLabelStyles}>
                                        Dispatched Date <Switch
                                            checked={dispatchedEnabled}
                                            onChange={onToggleDispatched} />
                                    </Typography>

                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container>
                                        <Grid item xs={10}>
                                            <Box sx={{ mb: 2 }}>
                                                <DatePicker
                                                    variant="standard"
                                                    label="From"
                                                    onChange={(value) => onAdvanced("Dispatched (From)", value)}
                                                    value={getValue("Dispatched (From)")}
                                                    nullable={false}
                                                />
                                            </Box>

                                            <DatePicker
                                                variant="standard"
                                                label="To"
                                                onChange={(value) => onAdvanced("Dispatched (To)", value)}
                                                value={getValue("Dispatched (To)")}
                                                nullable={false}
                                            />
                                        </Grid>
                                    </Grid>

                                </AccordionDetails>

                            </Accordion>

                        </>}


                    </Alert>

                </CardContent>
            </AnimateHeight>
        </Card >
    );

}